import { AdminPage } from './admin';
import { TermsAndConditionsWrapper } from './TermsAndConditionsWrapper';
import { CreateProgram } from './program-wizard/CreateProgram';
import { FileManager } from '../../dashboard/file-tracker/FileManager';
import { DashboardPage } from '../../dashboard/DashboardPage';
import { hasEntitlementsWithProductAsContext } from '../utils';
import {
  hasPermissionToRender,
  QuestTncAcceptedStatusStorageKey,
} from '@/app/core';
import { NavItem, useMainNav } from '@/app/core/components/main-nav';
import { PageNotFound } from '@/app/core/components/PageNotFound';
import { useDefaultLayoutState } from '@/app/store';
import { sessionCache } from '@/app/core/cache';
import { OktaUserInfo } from '@/app/core/auth';
import { AccessDenied } from '@/app/core/components/AccessDenied';
import {
  AiOutlineDashboard,
  AiOutlineTool,
  AiOutlineFileSync,
} from 'react-icons/ai/index.js';
import { AppRoles, FeatureCodes } from '@ssar-app/domain';
import { usePathContext } from '@ssar-app/okta-authn-react';
import { useEffect, useMemo, useCallback } from 'react';
import { Redirect, Route, Switch } from 'react-router';
import { Icon } from '@chakra-ui/react';

// eslint-disable-next-line @typescript-eslint/ban-types
export type QuestHomeProps = {};

export const QuestHome = (_props: QuestHomeProps) => {
  const { buildPath } = usePathContext();
  const isSiderCollapsed = useDefaultLayoutState().isSiderCollapsed;
  const { setNavItems } = useMainNav();

  const navItems = useMemo(() => {
    const allowAdminModule = hasPermissionToRender(
      FeatureCodes.AscendQuest,
      AppRoles.AscendQuest.Admin.permissions.MayViewAdminPage,
    );
    const toReturn: NavItem[] = [
      {
        key: 'quest-dashboard',
        icon: (
          <Icon
            as={AiOutlineDashboard}
            fontSize={isSiderCollapsed ? '2xl' : 'xl'}
          />
        ),
        to: buildPath('/dashboard'),
        title: 'Dashboard',
      },
    ];
    if (allowAdminModule) {
      toReturn.push({
        key: 'quest-admin',
        icon: (
          <Icon as={AiOutlineTool} fontSize={isSiderCollapsed ? '2xl' : 'xl'} />
        ),
        to: buildPath('/admin'),
        title: 'Admin',
      });
    }

    toReturn.push({
      key: 'quest-file-tracker',
      icon: (
        <Icon
          as={AiOutlineFileSync}
          fontSize={isSiderCollapsed ? '2xl' : 'xl'}
        />
      ),
      to: buildPath('/file-tracker'),
      title: 'File Tracker',
    });

    return toReturn;
  }, [buildPath, isSiderCollapsed]);

  useEffect(() => {
    setNavItems(navItems);
    return () => {
      setNavItems([]);
    };
  }, [navItems, setNavItems]);

  const shouldAcceptTnc = useMemo(() => {
    return !(sessionCache.get(QuestTncAcceptedStatusStorageKey) === '1');
  }, []);
  const onAccepted = useCallback(async () => {
    sessionCache.set(QuestTncAcceptedStatusStorageKey, '1');
  }, []);
  const canAccessProductRoutes = hasEntitlementsWithProductAsContext(
    FeatureCodes.AscendQuest,
    OktaUserInfo.getOktaUserInfo(),
  );

  const children = useMemo(() => {
    return (
      <Switch>
        <Route path={buildPath('/dashboard')}>
          <DashboardPage />
        </Route>
        <Route path={buildPath('/admin')}>
          <AdminPage />
        </Route>
        <Route path={buildPath('/file-tracker')}>
          <FileManager />
        </Route>
        <Route path={buildPath('/')} exact>
          <Redirect to={buildPath('/dashboard')} />
        </Route>
        <Route path={buildPath('/program/create')}>
          <CreateProgram />
        </Route>
        <Route path='*'>
          <PageNotFound />
        </Route>
      </Switch>
    );
  }, [buildPath]);

  const element = useMemo(() => {
    if (canAccessProductRoutes) {
      if (shouldAcceptTnc) {
        return (
          <TermsAndConditionsWrapper onAccepted={onAccepted}>
            {children}
          </TermsAndConditionsWrapper>
        );
      }
      return children;
    } else {
      return <AccessDenied />;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children, onAccepted, shouldAcceptTnc]);

  return element;
};

QuestHome.whyDidYouRender = true;
